import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  GraduationCap, Plus, Edit, Trash2, Search, Mail, Phone, Globe, AlertTriangle, 
  CheckCircle, XCircle, RefreshCw, Shield, AlertOctagon,
  Eye, Upload, Download, Send, Users, Sparkles, MailPlus, Check, Building,
  Calendar, Clock, FileText, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const RISK_LEVELS = {
  verified: { label: 'Verified', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  unverified: { label: 'Unverified', color: 'bg-slate-100 text-slate-700', icon: Shield },
  suspicious: { label: 'Suspicious', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  fake: { label: 'Fake/Fraudulent', color: 'bg-rose-100 text-rose-700', icon: AlertOctagon }
};

const STATUS_OPTIONS = [
  { value: 'prospect', label: 'Prospect', color: 'bg-blue-100 text-blue-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-amber-100 text-amber-700' },
  { value: 'partner', label: 'Partner', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'verified_partner', label: 'Verified Partner', color: 'bg-purple-100 text-purple-700' },
  { value: 'not_interested', label: 'Not Interested', color: 'bg-slate-100 text-slate-700' }
];

const DATE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' }
];

const MANDATORY_DOCS = [
  { id: 'degree_certificate', label: 'Degree Certificate' },
  { id: 'transcript', label: 'Transcript / Marksheet' },
  { id: 'provisional_certificate', label: 'Provisional Certificate' },
  { id: 'migration_certificate', label: 'Migration Certificate' },
  { id: 'convocation_letter', label: 'Convocation Letter' },
  { id: 'enrollment_letter', label: 'Enrollment Letter' },
  { id: 'others', label: 'Others' }
];

export default function InstitutionsTab() {
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showResearchDialog, setShowResearchDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [showBulkEmailDialog, setShowBulkEmailDialog] = useState(false);
  const [editingInstitution, setEditingInstitution] = useState(null);
  const [viewingInstitution, setViewingInstitution] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [webSearchQuery, setWebSearchQuery] = useState('');
  const [webSearchResults, setWebSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [contactedFilter, setContactedFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Selection state
  const [selectedInstitutions, setSelectedInstitutions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Bulk email state
  const [proposalTemplates, setProposalTemplates] = useState([]);
  const [emailForm, setEmailForm] = useState({
    subject: 'JustVerified - Education Verification Partnership',
    body: '',
    attachBrochure: true,
    attachPricing: true,
    selectedTemplate: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Bulk import state
  const [importData, setImportData] = useState('');
  const [importing, setImporting] = useState(false);
  
  const [formData, setFormData] = useState({
    institution_name: '',
    institution_type: 'university',
    contact_email: '',
    contact_phone: '',
    website: '',
    address: '',
    verification_email_template: '',
    notes: '',
    risk_level: 'unverified',
    ugc_recognized: false,
    aicte_approved: false,
    negative_info: '',
    last_verified_date: '',
    registrar_name: '',
    registrar_email: '',
    registrar_phone: '',
    // New fields
    status: 'prospect',
    contact_person: '',
    contact_person_email: '',
    contact_person_phone: '',
    contact_person_designation: '',
    brief: '',
    last_contacted_date: '',
    contact_history: [],
    mandatory_docs: [],
    created_at: '',
    updated_at: ''
  });

  useEffect(() => {
    fetchInstitutions();
    fetchProposalTemplates();
  }, []);

  const fetchInstitutions = async () => {
    try {
      const response = await axios.get('/institution-templates');
      setInstitutions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch institutions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProposalTemplates = async () => {
    try {
      const response = await axios.get('/proposal-templates');
      setProposalTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch proposal templates:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      institution_name: '',
      institution_type: 'university',
      contact_email: '',
      contact_phone: '',
      website: '',
      address: '',
      verification_email_template: '',
      notes: '',
      risk_level: 'unverified',
      ugc_recognized: false,
      aicte_approved: false,
      negative_info: '',
      last_verified_date: '',
      registrar_name: '',
      registrar_email: '',
      registrar_phone: '',
      status: 'prospect',
      contact_person: '',
      contact_person_email: '',
      contact_person_phone: '',
      contact_person_designation: '',
      brief: '',
      last_contacted_date: '',
      contact_history: [],
      mandatory_docs: []
    });
    setEditingInstitution(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (institution) => {
    setEditingInstitution(institution);
    setFormData({
      institution_name: institution.institution_name || '',
      institution_type: institution.institution_type || 'university',
      contact_email: institution.contact_email || '',
      contact_phone: institution.contact_phone || '',
      website: institution.website || '',
      address: institution.address || '',
      verification_email_template: institution.verification_email_template || '',
      notes: institution.notes || '',
      risk_level: institution.risk_level || 'unverified',
      ugc_recognized: institution.ugc_recognized || false,
      aicte_approved: institution.aicte_approved || false,
      negative_info: institution.negative_info || '',
      last_verified_date: institution.last_verified_date || '',
      registrar_name: institution.registrar_name || '',
      registrar_email: institution.registrar_email || '',
      registrar_phone: institution.registrar_phone || '',
      status: institution.status || 'prospect',
      contact_person: institution.contact_person || '',
      contact_person_email: institution.contact_person_email || '',
      contact_person_phone: institution.contact_person_phone || '',
      contact_person_designation: institution.contact_person_designation || '',
      brief: institution.brief || '',
      last_contacted_date: institution.last_contacted_date || '',
      contact_history: institution.contact_history || [],
      mandatory_docs: institution.mandatory_docs || []
    });
    setShowDialog(true);
  };

  const openViewDialog = (institution) => {
    setViewingInstitution(institution);
    setShowViewDialog(true);
  };

  // Date filter helper
  const isWithinDateRange = (dateStr) => {
    if (!dateStr) return dateFilter === 'all';
    
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (dateFilter) {
      case 'today':
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);
        return date >= today && date <= todayEnd;
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        return date >= weekStart;
      case 'month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return date >= monthStart;
      case 'custom':
        const from = dateFrom ? new Date(dateFrom) : null;
        const to = dateTo ? new Date(dateTo) : null;
        if (from) from.setHours(0, 0, 0, 0);
        if (to) to.setHours(23, 59, 59, 999);
        if (from && to) return date >= from && date <= to;
        if (from) return date >= from;
        if (to) return date <= to;
        return true;
      default:
        return true;
    }
  };

  const toggleMandatoryDoc = (docId) => {
    setFormData(prev => {
      const docs = prev.mandatory_docs || [];
      if (docs.includes(docId)) {
        return { ...prev, mandatory_docs: docs.filter(d => d !== docId) };
      } else {
        return { ...prev, mandatory_docs: [...docs, docId] };
      }
    });
  };

  const handleSubmit = async () => {
    if (!formData.institution_name) {
      toast.error('Institution name is required');
      return;
    }

    try {
      const payload = {
        ...formData,
        last_verified_date: formData.last_verified_date || new Date().toISOString().split('T')[0]
      };
      
      if (editingInstitution) {
        await axios.put(`/institution-templates/${editingInstitution.id}`, payload);
        toast.success('Institution updated successfully');
      } else {
        await axios.post('/institution-templates', payload);
        toast.success('Institution added successfully');
      }
      setShowDialog(false);
      resetForm();
      fetchInstitutions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save institution');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this institution?')) return;
    
    try {
      await axios.delete(`/institution-templates/${id}`);
      toast.success('Institution deleted');
      fetchInstitutions();
    } catch (error) {
      toast.error('Failed to delete institution');
    }
  };

  // AI Research using Gemini
  const handleWebResearch = async () => {
    if (!webSearchQuery.trim()) {
      toast.error('Please enter a search query');
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.post('/research/institution', { query: webSearchQuery });
      setWebSearchResults(response.data?.results || []);
      if (response.data?.results?.length === 0) {
        toast.info('No results found. Try a different search term.');
      }
    } catch (error) {
      toast.error('Research failed');
    } finally {
      setSearching(false);
    }
  };

  const importFromResearch = (result) => {
    setFormData({
      ...formData,
      institution_name: result.institution_name || '',
      website: result.website || '',
      institution_type: result.institution_type || 'university',
      address: result.address || '',
      contact_email: result.contact_email || '',
      registrar_email: result.registrar_email || '',
      risk_level: result.risk_level || 'unverified',
      ugc_recognized: result.ugc_recognized || false,
      aicte_approved: result.aicte_approved || false,
      brief: `Imported from AI research on ${new Date().toLocaleDateString()}`
    });
    setShowResearchDialog(false);
    setShowDialog(true);
    toast.success('Data imported! Please review and save.');
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedInstitutions(filteredInstitutions.map(i => i.id));
    } else {
      setSelectedInstitutions([]);
    }
  };

  const handleSelectInstitution = (instId, checked) => {
    if (checked) {
      setSelectedInstitutions([...selectedInstitutions, instId]);
    } else {
      setSelectedInstitutions(selectedInstitutions.filter(id => id !== instId));
      setSelectAll(false);
    }
  };

  // Bulk Import
  const handleBulkImport = async () => {
    if (!importData.trim()) {
      toast.error('Please paste CSV data');
      return;
    }
    
    setImporting(true);
    try {
      const lines = importData.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      let imported = 0;
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const inst = {};
        headers.forEach((header, idx) => {
          inst[header.replace(/\s+/g, '_')] = values[idx] || '';
        });
        
        if (inst.institution_name) {
          await axios.post('/institution-templates', {
            institution_name: inst.institution_name,
            institution_type: inst.institution_type || 'university',
            contact_email: inst.contact_email || inst.email || '',
            contact_phone: inst.contact_phone || inst.phone || '',
            website: inst.website || '',
            status: inst.status || 'prospect',
            contact_person: inst.contact_person || '',
            brief: inst.brief || inst.description || '',
            risk_level: 'unverified'
          });
          imported++;
        }
      }
      
      toast.success(`Imported ${imported} institutions successfully`);
      setShowBulkImportDialog(false);
      setImportData('');
      fetchInstitutions();
    } catch (error) {
      toast.error('Import failed: ' + (error.message || 'Unknown error'));
    } finally {
      setImporting(false);
    }
  };

  // Bulk Email
  const handleBulkEmail = async () => {
    if (selectedInstitutions.length === 0) {
      toast.error('Please select institutions to email');
      return;
    }
    
    setSendingEmail(true);
    try {
      const selectedData = institutions.filter(i => selectedInstitutions.includes(i.id));
      
      for (const inst of selectedData) {
        const emailTo = inst.contact_person_email || inst.contact_email || inst.registrar_email;
        if (!emailTo) continue;
        
        await axios.post('/send-email', {
          to_email: emailTo,
          subject: emailForm.subject,
          body: emailForm.body,
          attachments: {
            brochure: emailForm.attachBrochure,
            pricing: emailForm.attachPricing,
            proposal_template_id: emailForm.selectedTemplate
          }
        });
        
        // Mark as contacted
        await axios.put(`/institution-templates/${inst.id}`, {
          ...inst,
          status: inst.status === 'prospect' ? 'contacted' : inst.status,
          last_contacted_date: new Date().toISOString().split('T')[0],
          contact_history: [
            ...(inst.contact_history || []),
            { date: new Date().toISOString(), type: 'email', subject: emailForm.subject }
          ]
        });
      }
      
      toast.success(`Emails sent to ${selectedData.filter(i => i.contact_person_email || i.contact_email || i.registrar_email).length} institutions`);
      setShowBulkEmailDialog(false);
      setSelectedInstitutions([]);
      setSelectAll(false);
      fetchInstitutions();
    } catch (error) {
      toast.error('Failed to send some emails');
    } finally {
      setSendingEmail(false);
    }
  };

  // Download CSV Template
  const downloadTemplate = () => {
    const template = 'institution_name,institution_type,contact_email,contact_phone,website,contact_person,brief,status\nExample University,university,registrar@example.edu,+91-9876543210,https://example.edu,John Doe,Brief description,prospect';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'institution_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  // Export Institutions
  const exportInstitutions = () => {
    const dataToExport = selectedInstitutions.length > 0 
      ? institutions.filter(i => selectedInstitutions.includes(i.id))
      : filteredInstitutions;
    
    const headers = ['institution_name', 'institution_type', 'contact_email', 'website', 'contact_person', 'status', 'brief'];
    const csv = [
      headers.join(','),
      ...dataToExport.map(i => headers.map(h => `"${(i[h] || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'institutions_export.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${dataToExport.length} institutions`);
  };

  const filteredInstitutions = institutions.filter(inst => {
    const matchesSearch = inst.institution_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inst.contact_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inst.contact_person?.toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchesRisk = true;
    if (activeTab === 'verified') matchesRisk = inst.risk_level === 'verified';
    else if (activeTab === 'suspicious') matchesRisk = inst.risk_level === 'suspicious' || inst.risk_level === 'fake';
    else if (activeTab === 'unverified') matchesRisk = inst.risk_level === 'unverified';
    
    const matchesStatus = statusFilter === 'all' || inst.status === statusFilter;
    
    let matchesContacted = true;
    if (contactedFilter === 'contacted') matchesContacted = !!inst.last_contacted_date;
    else if (contactedFilter === 'not_contacted') matchesContacted = !inst.last_contacted_date;
    
    const matchesDate = isWithinDateRange(inst.created_at);
    
    return matchesSearch && matchesRisk && matchesStatus && matchesContacted && matchesDate;
  });

  const getRiskBadge = (risk_level) => {
    const risk = RISK_LEVELS[risk_level] || RISK_LEVELS.unverified;
    const Icon = risk.icon;
    return <Badge className={`${risk.color} flex items-center gap-1`}><Icon className="w-3 h-3" />{risk.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const statusOption = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return <Badge className={statusOption.color}>{statusOption.label}</Badge>;
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Universities & Institutions</h2>
          <p className="text-slate-600">Education verification contacts - Auto-initiate when institution matches</p>
        </div>
        <Badge className="bg-emerald-100 text-emerald-700 text-lg px-4 py-2">
          {institutions.length} Institutions
        </Badge>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button variant="outline" onClick={() => setShowResearchDialog(true)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
          <Sparkles className="w-4 h-4 mr-2" />AI Research
        </Button>
        <Button variant="outline" onClick={() => setShowBulkImportDialog(true)}>
          <Upload className="w-4 h-4 mr-2" />Bulk Import
        </Button>
        <Button variant="outline" onClick={exportInstitutions}>
          <Download className="w-4 h-4 mr-2" />Export
        </Button>
        <Button 
          variant="outline" 
          onClick={() => setShowBulkEmailDialog(true)}
          disabled={selectedInstitutions.length === 0}
          className={selectedInstitutions.length > 0 ? 'border-emerald-300 text-emerald-700' : ''}
        >
          <MailPlus className="w-4 h-4 mr-2" />
          Bulk Email {selectedInstitutions.length > 0 && `(${selectedInstitutions.length})`}
        </Button>
        <Button onClick={openCreateDialog} className="bg-emerald-600 hover:bg-emerald-700 ml-auto">
          <Plus className="w-4 h-4 mr-2" />Add Institution
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search institutions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUS_OPTIONS.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={contactedFilter} onValueChange={setContactedFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Contacted" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="not_contacted">Not Contacted</SelectItem>
              </SelectContent>
            </Select>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-[150px]">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Date Added" />
              </SelectTrigger>
              <SelectContent>
                {DATE_FILTER_OPTIONS.map(d => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {dateFilter === 'custom' && (
              <>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-[140px]"
                  placeholder="From"
                />
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-[140px]"
                  placeholder="To"
                />
              </>
            )}
            <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(''); setStatusFilter('all'); setContactedFilter('all'); setActiveTab('all'); setDateFilter('all'); setDateFrom(''); setDateTo(''); }}>
              <XCircle className="w-4 h-4 mr-1" />Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Risk Level Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="all">All ({institutions.length})</TabsTrigger>
          <TabsTrigger value="verified">Verified ({institutions.filter(i => i.risk_level === 'verified').length})</TabsTrigger>
          <TabsTrigger value="unverified">Unverified ({institutions.filter(i => i.risk_level === 'unverified').length})</TabsTrigger>
          <TabsTrigger value="suspicious">Suspicious ({institutions.filter(i => i.risk_level === 'suspicious' || i.risk_level === 'fake').length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Institutions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-emerald-600" />
            Institution Records
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredInstitutions.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <GraduationCap className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No institutions found. Use AI Research or Add Institution to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox checked={selectAll} onCheckedChange={handleSelectAll} />
                  </TableHead>
                  <TableHead>Institution Name</TableHead>
                  <TableHead>Contact Person</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Brief</TableHead>
                  <TableHead>Risk</TableHead>
                  <TableHead>Last Contacted</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInstitutions.map((inst) => (
                  <TableRow key={inst.id} className={inst.risk_level === 'fake' ? 'bg-rose-50' : inst.risk_level === 'suspicious' ? 'bg-amber-50' : ''}>
                    <TableCell>
                      <Checkbox
                        checked={selectedInstitutions.includes(inst.id)}
                        onCheckedChange={(checked) => handleSelectInstitution(inst.id, checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <GraduationCap className="w-4 h-4 text-slate-400" />
                        <div>
                          <div className="font-medium">{inst.institution_name}</div>
                          <div className="flex gap-1 mt-1">
                            {inst.ugc_recognized && <Badge variant="outline" className="text-xs bg-green-50">UGC</Badge>}
                            {inst.aicte_approved && <Badge variant="outline" className="text-xs bg-blue-50">AICTE</Badge>}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {inst.contact_person && (
                        <div>
                          <div className="font-medium text-sm">{inst.contact_person}</div>
                          {inst.contact_person_designation && <div className="text-xs text-slate-500">{inst.contact_person_designation}</div>}
                          {inst.contact_person_email && <div className="text-xs text-blue-600">{inst.contact_person_email}</div>}
                        </div>
                      )}
                      {!inst.contact_person && inst.registrar_name && (
                        <div className="text-sm">{inst.registrar_name}</div>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(inst.status)}</TableCell>
                    <TableCell>
                      <p className="text-sm text-slate-600 max-w-[200px] truncate" title={inst.brief}>
                        {inst.brief || '-'}
                      </p>
                    </TableCell>
                    <TableCell>{getRiskBadge(inst.risk_level)}</TableCell>
                    <TableCell>
                      {inst.last_contacted_date ? (
                        <div className="text-sm text-emerald-600 flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          {new Date(inst.last_contacted_date).toLocaleDateString()}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">Never</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openViewDialog(inst)} title="View Details">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(inst)} title="Edit">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => handleDelete(inst.id)} title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5" />
              {viewingInstitution?.institution_name}
            </DialogTitle>
          </DialogHeader>
          {viewingInstitution && (
            <div className="space-y-6 py-4">
              <div className="flex gap-2 flex-wrap">
                {getRiskBadge(viewingInstitution.risk_level)}
                {getStatusBadge(viewingInstitution.status)}
                {viewingInstitution.ugc_recognized && <Badge className="bg-green-100 text-green-700">UGC Recognized</Badge>}
                {viewingInstitution.aicte_approved && <Badge className="bg-blue-100 text-blue-700">AICTE Approved</Badge>}
                {viewingInstitution.auto_initiate && <Badge className="bg-green-100 text-green-700">Auto-Initiate</Badge>}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-700">Institution Details</h4>
                  <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                    {viewingInstitution.institution_type && <p><strong>Type:</strong> {viewingInstitution.institution_type}</p>}
                    {viewingInstitution.university_name && <p><strong>University:</strong> {viewingInstitution.university_name}</p>}
                    {(viewingInstitution.website || viewingInstitution.website_url) && (
                      <p className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-slate-500" />
                        <strong>Website:</strong> 
                        <a href={viewingInstitution.website || viewingInstitution.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          {viewingInstitution.website || viewingInstitution.website_url}
                        </a>
                      </p>
                    )}
                    {viewingInstitution.address && <p><strong>Address:</strong> {viewingInstitution.address}</p>}
                    {viewingInstitution.ugc_code && <p><strong>UGC Code:</strong> {viewingInstitution.ugc_code}</p>}
                    {viewingInstitution.aicte_code && <p><strong>AICTE Code:</strong> {viewingInstitution.aicte_code}</p>}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-700">Registrar / Verification Contact</h4>
                  <div className="bg-emerald-50 p-4 rounded-lg space-y-2">
                    {viewingInstitution.registrar_name && (
                      <p className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-emerald-500" />
                        <strong>Name:</strong> {viewingInstitution.registrar_name}
                      </p>
                    )}
                    {viewingInstitution.registrar_email && (
                      <p className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-emerald-500" />
                        <strong>Email:</strong> 
                        <a href={`mailto:${viewingInstitution.registrar_email}`} className="text-blue-600 hover:underline">{viewingInstitution.registrar_email}</a>
                      </p>
                    )}
                    {(viewingInstitution.registrar_phone || viewingInstitution.contact_phone) && (
                      <p className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-emerald-500" />
                        <strong>Phone:</strong> {viewingInstitution.registrar_phone || viewingInstitution.contact_phone}
                      </p>
                    )}
                    {viewingInstitution.contact_email && (
                      <p className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-emerald-500" />
                        <strong>General Email:</strong> 
                        <a href={`mailto:${viewingInstitution.contact_email}`} className="text-blue-600 hover:underline">{viewingInstitution.contact_email}</a>
                      </p>
                    )}
                    {viewingInstitution.verification_process && (
                      <p><strong>Verification Process:</strong> {viewingInstitution.verification_process}</p>
                    )}
                    {viewingInstitution.verification_portal_url && (
                      <p className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-emerald-500" />
                        <strong>Portal:</strong>
                        <a href={viewingInstitution.verification_portal_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          {viewingInstitution.verification_portal_url}
                        </a>
                      </p>
                    )}
                    {viewingInstitution.verification_fee && <p><strong>Verification Fee:</strong> {viewingInstitution.verification_fee}</p>}
                    {viewingInstitution.typical_tat_days && <p><strong>Typical TAT:</strong> {viewingInstitution.typical_tat_days} days</p>}
                    {!viewingInstitution.registrar_email && !viewingInstitution.registrar_name && !viewingInstitution.contact_email && (
                      <p className="text-slate-500">No registrar contact assigned</p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Sales Contact Person */}
              {(viewingInstitution.contact_person || viewingInstitution.contact_person_email) && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-700">Sales Contact Person</h4>
                  <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                    <div className="grid grid-cols-2 gap-4">
                      {viewingInstitution.contact_person && <p><strong>Name:</strong> {viewingInstitution.contact_person}</p>}
                      {viewingInstitution.contact_person_designation && <p><strong>Designation:</strong> {viewingInstitution.contact_person_designation}</p>}
                      {viewingInstitution.contact_person_email && (
                        <p className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-500" />
                          <strong>Email:</strong> 
                          <a href={`mailto:${viewingInstitution.contact_person_email}`} className="text-blue-600 hover:underline">{viewingInstitution.contact_person_email}</a>
                        </p>
                      )}
                      {viewingInstitution.contact_person_phone && (
                        <p className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-slate-500" />
                          <strong>Phone:</strong> {viewingInstitution.contact_person_phone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Mandatory Documents */}
              {viewingInstitution.mandatory_docs && viewingInstitution.mandatory_docs.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-700">Mandatory Documents</h4>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <div className="flex flex-wrap gap-2">
                      {viewingInstitution.mandatory_docs.map(docId => {
                        const doc = MANDATORY_DOCS.find(d => d.id === docId);
                        return doc ? (
                          <Badge key={docId} variant="outline" className="bg-blue-50">
                            <FileText className="w-3 h-3 mr-1" />{doc.label}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                </div>
              )}
              
              {viewingInstitution.brief && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-700">Brief / Notes</h4>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p>{viewingInstitution.brief}</p>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
            <Button onClick={() => { setShowViewDialog(false); openEditDialog(viewingInstitution); }}>
              <Edit className="w-4 h-4 mr-2" />Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Research Dialog */}
      <Dialog open={showResearchDialog} onOpenChange={setShowResearchDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-600" />
              AI Research - Find Institution Data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Input
                value={webSearchQuery}
                onChange={(e) => setWebSearchQuery(e.target.value)}
                placeholder="Enter institution name to research..."
                onKeyDown={(e) => e.key === 'Enter' && handleWebResearch()}
              />
              <Button onClick={handleWebResearch} disabled={searching} className="bg-emerald-600 hover:bg-emerald-700">
                {searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Research
              </Button>
            </div>
            
            <p className="text-sm text-slate-500">
              Uses Gemini AI to search UGC, AICTE databases and public sources for institution details and verification contacts.
            </p>
            
            {webSearchResults.length > 0 && (
              <div className="border rounded-lg divide-y max-h-80 overflow-y-auto">
                {webSearchResults.map((result, idx) => (
                  <div key={idx} className="p-4 hover:bg-slate-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{result.institution_name}</div>
                        {result.institution_type && <div className="text-sm text-slate-600">{result.institution_type}</div>}
                        {result.website && <div className="text-sm text-blue-600">{result.website}</div>}
                        {result.address && <div className="text-sm text-slate-500">{result.address}</div>}
                        <div className="flex gap-2 mt-1">
                          {result.ugc_recognized && <Badge variant="outline" className="text-xs">UGC</Badge>}
                          {result.aicte_approved && <Badge variant="outline" className="text-xs">AICTE</Badge>}
                        </div>
                      </div>
                      <Button size="sm" onClick={() => importFromResearch(result)}>
                        <Plus className="w-4 h-4 mr-1" />Import
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResearchDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-emerald-600" />
              Bulk Import Institutions
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Button variant="outline" onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-2" />Download Template
            </Button>
            <div>
              <Label>Paste CSV Data</Label>
              <Textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder="institution_name,institution_type,contact_email,website,contact_person,brief,status&#10;Example University,university,registrar@example.edu,..."
                rows={10}
                className="font-mono text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkImportDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkImport} disabled={importing} className="bg-emerald-600 hover:bg-emerald-700">
              {importing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Email Dialog */}
      <Dialog open={showBulkEmailDialog} onOpenChange={setShowBulkEmailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MailPlus className="w-5 h-5 text-emerald-600" />
              Send Bulk Email ({selectedInstitutions.length} selected)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Subject</Label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
              />
            </div>
            <div>
              <Label>Message Body</Label>
              <Textarea
                value={emailForm.body}
                onChange={(e) => setEmailForm({...emailForm, body: e.target.value})}
                placeholder="Enter your email message..."
                rows={6}
              />
            </div>
            <div>
              <Label>Select Proposal Template</Label>
              <Select
                value={emailForm.selectedTemplate}
                onValueChange={(v) => setEmailForm({...emailForm, selectedTemplate: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a proposal template (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No template</SelectItem>
                  {proposalTemplates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={emailForm.attachBrochure}
                  onCheckedChange={(checked) => setEmailForm({...emailForm, attachBrochure: checked})}
                />
                <span className="text-sm">Attach Brochure</span>
              </label>
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={emailForm.attachPricing}
                  onCheckedChange={(checked) => setEmailForm({...emailForm, attachPricing: checked})}
                />
                <span className="text-sm">Attach Pricing Sheet</span>
              </label>
            </div>
            <div className="bg-amber-50 p-3 rounded-lg text-sm">
              <p className="text-amber-600">Institutions will be marked as "Contacted" after sending emails.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkEmailDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkEmail} disabled={sendingEmail} className="bg-emerald-600 hover:bg-emerald-700">
              {sendingEmail ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send Emails
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingInstitution ? 'Edit Institution' : 'Add New Institution'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Institution Name *</Label>
              <Input
                value={formData.institution_name}
                onChange={(e) => setFormData({...formData, institution_name: e.target.value})}
                placeholder="e.g., Indian Institute of Technology Delhi"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={formData.institution_type} onValueChange={(v) => setFormData({...formData, institution_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="university">University</SelectItem>
                  <SelectItem value="college">College</SelectItem>
                  <SelectItem value="institute">Institute</SelectItem>
                  <SelectItem value="school">School</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Website</Label>
              <Input value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} />
            </div>
            <div>
              <Label>Risk Level</Label>
              <Select value={formData.risk_level} onValueChange={(v) => setFormData({...formData, risk_level: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="unverified">Unverified</SelectItem>
                  <SelectItem value="suspicious">Suspicious</SelectItem>
                  <SelectItem value="fake">Fake/Fraudulent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-4 col-span-2">
              <label className="flex items-center gap-2">
                <Checkbox checked={formData.ugc_recognized} onCheckedChange={(c) => setFormData({...formData, ugc_recognized: c})} />
                <span className="text-sm">UGC Recognized</span>
              </label>
              <label className="flex items-center gap-2">
                <Checkbox checked={formData.aicte_approved} onCheckedChange={(c) => setFormData({...formData, aicte_approved: c})} />
                <span className="text-sm">AICTE Approved</span>
              </label>
            </div>
            
            <div className="col-span-2 border-t pt-4 mt-2">
              <h4 className="font-medium mb-3 flex items-center gap-2"><Users className="w-4 h-4" />Contact Person</h4>
            </div>
            <div>
              <Label>Name</Label>
              <Input value={formData.contact_person} onChange={(e) => setFormData({...formData, contact_person: e.target.value})} />
            </div>
            <div>
              <Label>Designation</Label>
              <Input value={formData.contact_person_designation} onChange={(e) => setFormData({...formData, contact_person_designation: e.target.value})} placeholder="e.g., Registrar" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={formData.contact_person_email} onChange={(e) => setFormData({...formData, contact_person_email: e.target.value})} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={formData.contact_person_phone} onChange={(e) => setFormData({...formData, contact_person_phone: e.target.value})} />
            </div>
            
            <div className="col-span-2">
              <Label>Brief / Notes</Label>
              <Textarea
                value={formData.brief}
                onChange={(e) => setFormData({...formData, brief: e.target.value})}
                placeholder="Brief description or notes..."
                rows={3}
              />
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <Textarea value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} rows={2} />
            </div>
            
            {/* Mandatory Documents */}
            <div className="col-span-2 border-t pt-4 mt-2">
              <h4 className="font-medium mb-3 flex items-center gap-2"><FileText className="w-4 h-4" />Mandatory Documents for Verification</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {MANDATORY_DOCS.map(doc => (
                  <label key={doc.id} className="flex items-center gap-2 p-2 border rounded-lg hover:bg-slate-50 cursor-pointer">
                    <Checkbox
                      checked={(formData.mandatory_docs || []).includes(doc.id)}
                      onCheckedChange={() => toggleMandatoryDoc(doc.id)}
                    />
                    <span className="text-sm">{doc.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-700">
              {editingInstitution ? 'Update' : 'Add'} Institution
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
